import { z } from 'zod'

import { REGIONS_BY_ID, type RegionId, regionIdSchema } from './region'

/** Single source of truth for all staff role values. Use STAFF_ROLE.x instead of raw strings. */
export const STAFF_ROLE = {
  customer_care: 'customer_care',
  vca: 'vca',
  nurse: 'nurse',
  vet: 'vet',
  remote_vet: 'remote_vet',
  admin: 'admin',
} as const

export const staffRoleSchema = z.enum(Object.values(STAFF_ROLE))
export type StaffRole = z.infer<typeof staffRoleSchema>

export const STAFF_ROLE_LABELS: Record<StaffRole, string> = {
  [STAFF_ROLE.customer_care]: 'Customer Care',
  [STAFF_ROLE.vca]: 'VCA',
  [STAFF_ROLE.nurse]: 'Nurse',
  [STAFF_ROLE.vet]: 'Vet',
  [STAFF_ROLE.remote_vet]: 'Remote Vet',
  [STAFF_ROLE.admin]: 'Admin',
}

export function isVet(staff: { role: StaffRole }) {
  return staff.role === STAFF_ROLE.vet || staff.role === STAFF_ROLE.remote_vet
}

export const STAFF_ROLES = staffRoleSchema.options.map((value) => ({
  value,
  label: STAFF_ROLE_LABELS[value],
}))

/**
 * Staff roles whose work is intrinsically scoped to a single physical clinic.
 * Other roles (customer care, remote vet, admin) operate across clinics and so
 * have no `assigneeClinicId` on their tasks.
 */
export const CLINIC_STAFF_ROLES = new Set<StaffRole>([
  STAFF_ROLE.vet,
  STAFF_ROLE.vca,
  STAFF_ROLE.nurse,
])

/**
 * Visibility level for a staff member in booking flows. Currently only 'public'
 * exists — null in the DB means not publicly bookable, which is a schema-level
 * absence rather than a named domain state.
 */
export const staffBookingVisibilitySchema = z.literal('public')
export type StaffBookingVisibility = z.infer<typeof staffBookingVisibilitySchema>

export const staffMemberSchema = z.object({
  id: z.string(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  role: staffRoleSchema,
  isSuperuser: z.boolean(),
  /** Professional license / registration number — only relevant for vets. Region-specific (RCVS in the UK, state license in the US). */
  licenseNumber: z.string().nullable(),
  /** Regions this staff member belongs to (at least one). */
  regions: z.array(regionIdSchema).min(1),
  /** Whether this staff member is visible in public-facing booking flows. */
  bookingVisibility: staffBookingVisibilitySchema.nullable(),
  /** Whether the vet has an e-signature image on file. */
  hasEsignature: z.boolean(),
})

export type StaffMember = z.infer<typeof staffMemberSchema>

/**
 * Identifies "locum" vets — virtual vets that, unlike physical-clinic vets, can
 * be booked at more than one clinic at the same time. For now they are recognised
 * purely by name: any staff member whose first name contains "locum"
 * (case-insensitive).
 *
 * Availability overlap checks (existing appointments + blocked slots) are scoped to
 * the contextual clinic for locum vets only; all other staff are checked across every
 * clinic, since a real vet can only physically be in one place at a time.
 */
export function isLocumVet(firstName: string | null | undefined): boolean {
  return typeof firstName === 'string' && firstName.toLowerCase().includes('locum')
}

/** Convenience helper mirroring CLINIC_STAFF_ROLES membership. */
export function staffRoleRequiresClinic(role: StaffRole): boolean {
  return CLINIC_STAFF_ROLES.has(role)
}

/**
 * Resolve the region-appropriate label for a staff member's professional
 * registration field (e.g. "RCVS number" in the UK, "NJ license number" in the US).
 *
 * NOTE: assumes a single region per vet — it uses the first region. A vet
 * assigned to multiple regions gets the label of whichever region is first in
 * the array. This matches the current one-region-per-vet reality; revisit if
 * multi-region vets become real.
 */
export function getStaffRegistrationLabel(regions: RegionId[]): string {
  return REGIONS_BY_ID[regions[0]]?.staffRegistrationLabel ?? 'License number'
}
